/*
 * @Author: Yun
 * @Date: 2025-11-21 17:24:49
 * @Last Modified by:   Yun
 * @Last Modified time: 2025-11-21 17:24:49
 */

import React, { useEffect, useState, useRef } from 'react';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { gsap } from 'gsap';
import { content } from '@configs/content';
import styles from './index.module.scss';

const NavBar: React.FC = () => {
    const { logo, navItems } = content.navBar;
    const [smoother, setSmoother] = useState<ScrollSmoother | null>(null);
    const [activeSection, setActiveSection] = useState<string>('hero');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const instance = ScrollSmoother.get();
        if (instance) {
            setSmoother(instance);
        }

        // 监听滚动，更新active状态
        const handleScroll = () => {
            const scrollPosition = window.scrollY;

            // 如果在顶部，active为hero
            if (scrollPosition < 100) {
                setActiveSection('hero');
                return;
            }

            const sections = navItems
                .map((item) => ({
                    id: item.sectionId,
                    element: document.getElementById(item.sectionId),
                }))
                .filter((section) => section.element !== null);

            // 找到当前在视窗中的section
            let currentSection = 'hero';

            for (const section of sections) {
                if (section.element) {
                    // 计算元素的真实offsetTop
                    let element: HTMLElement | null = section.element;
                    let offsetTop = 0;

                    while (element) {
                        offsetTop += element.offsetTop;
                        element = element.offsetParent as HTMLElement | null;
                    }

                    // 如果滚动位置超过了section的顶部（考虑导航栏高度）
                    if (scrollPosition + 150 >= offsetTop) {
                        currentSection = section.id;
                    }
                }
            }

            setActiveSection(currentSection);
        };

        // 初始化时执行一次
        handleScroll();

        // 添加滚动监听
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [navItems]);

    const scrollToSection = (sectionId: string) => {
        setActiveSection(sectionId);
        setIsMobileMenuOpen(false); // 关闭移动端菜单

        // 查找目标元素（可能在pin-spacer内部）
        const targetElement = document.getElementById(sectionId);

        if (!targetElement) {
            console.warn(`Element with id "${sectionId}" not found`);
            return;
        }

        if (smoother) {
            // 获取元素在文档中的实际位置
            let element: HTMLElement | null = targetElement;
            let offsetTop = 0;

            // 累加所有父元素的offsetTop，获取真实位置
            while (element) {
                offsetTop += element.offsetTop;
                element = element.offsetParent as HTMLElement | null;
            }

            // Hero组件滚动到顶部（0），其他组件滚动到其顶部
            const targetPosition = sectionId === 'hero' ? 0 : offsetTop;

            smoother.scrollTo(targetPosition, true);
        } else {
            // 降级方案：使用原生滚动
            let element: HTMLElement | null = targetElement;
            let offsetTop = 0;

            while (element) {
                offsetTop += element.offsetTop;
                element = element.offsetParent as HTMLElement | null;
            }

            const targetPosition = sectionId === 'hero' ? 0 : offsetTop;
            window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }
    };

    return (
        <nav className={styles.navBar}>
            <div className={styles.navContainer}>
                <div className={styles.logoContainer}>
                    <img src={logo} alt="CGTN" className={styles.logo} />
                </div>

                {/* PC端导航按钮 */}
                <div className={styles.navItems}>
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            className={`${styles.navItem} ${activeSection === item.sectionId ? styles.active : ''}`}
                            onClick={() => scrollToSection(item.sectionId)}>
                            {item.label}
                        </button>
                    ))}
                </div>

                {/* 移动端汉堡按钮 */}
                <button 
                    className={`${styles.hamburger} ${isMobileMenuOpen ? styles.open : ''}`}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>

            {/* 移动端全屏菜单 */}
            <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.open : ''}`}>
                <div className={styles.mobileMenuContent}>
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            className={`${styles.mobileMenuItem} ${activeSection === item.sectionId ? styles.active : ''}`}
                            onClick={() => scrollToSection(item.sectionId)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>
        </nav>
    );
};

export default NavBar;
